// Orders to suppliers, in words (Back office sweep, Oct 2026: D10).

import { qty } from './units';
import { deliveryWords } from './delivery';

export { qty };

// A sent order's stage comes from the server (`buying.stage`): sent, chase (not confirmed by
// their cut-off), confirmed, due (confirmed, and the day has come).
export function orderStatus(o) {
  if (o.status === 'draft') return { words: 'Draft', tone: null };
  if (o.stage === 'chase') return { words: 'Not confirmed', tone: 'warn' };
  if (o.stage === 'confirmed') return { words: 'Confirmed', tone: 'ok' };
  if (o.stage === 'due') return { words: 'Due', tone: 'ok' };
  if (o.status === 'sent') return { words: 'Sent', tone: 'info' };
  if (o.status === 'part_received') return { words: `Received · ${o.short} short`, tone: 'warn' };
  if (o.status === 'received') return { words: 'Received', tone: 'ok' };
  return { words: o.status, tone: null };
}

// What came, said back before it's saved: "Double cream goes from 4 L to 6 L."
export function stockWords(lines, stockById) {
  return lines.filter((l) => Number(l.received) > 0).map((l) => {
    const s = stockById[l.stock_id];
    if (!s) return null;
    return `${l.name} goes from ${qty(s.quantity, s.unit)} to ${qty(Number(s.quantity) + Number(l.received), s.unit)}.`;
  }).filter(Boolean);
}

// A supplier's, said properly: "Brakes's" reads wrong, so a name ending in s takes the
// apostrophe alone ("Hartley's Butchers' delivery days").
export const theirs = (name) => `${name}${/s$/i.test(name) ? "'" : "'s"}`;

const hhmm = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
const sameDay = (a, b) => a.toDateString() === b.toDateString();
// "09:12" today, "Mon 26 Oct" before.
export function whenWords(iso, now = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return sameDay(d, now) ? hhmm(d) : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// The lines they said they'd send less of.
export const shortLines = (o) => (o.lines || []).filter((l) => l.confirmed != null && Number(l.confirmed) < Number(l.quantity));

// Under the status, what happened last: "emailed 09:12 · their cut-off was 16:00", "Jo replied
// · hake for cod", "sent by hand".
export function statusNote(o, now = new Date()) {
  if (o.status === 'draft') return `started ${whenWords(o.created_at, now)}`;
  if (o.status !== 'sent') return '';
  const how = o.sent_to ? `emailed ${whenWords(o.sent_at, now)}` : 'sent by hand';
  if (o.stage === 'confirmed' || o.stage === 'due') {
    const short = shortLines(o).length;
    return [o.confirm_note || `confirmed ${whenWords(o.confirmed_at, now)}`, short && `${short} short`].filter(Boolean).join(' · ');
  }
  const bits = [how];
  if (o.stage === 'chase') bits.push(o.cut_off ? `their cut-off was ${hhmm(new Date(o.cut_off))}` : 'no reply yet');
  if (o.chased_at) bits.push(`chased ${whenWords(o.chased_at, now)}`);
  return bits.join(' · ');
}

// What to do with it next.
export function nextStep(o) {
  if (o.status === 'draft') return 'send';
  if (o.stage === 'chase') return 'chase';
  if (o.stage === 'sent') return 'confirm';
  if (o.stage === 'confirmed' || o.stage === 'due') return 'receive';
  return null;
}

// Today's tiles: what's due in today (sent, not yet received), and what wants chasing.
export const dueToday = (orders, today) => (orders || []).filter((o) => o.status === 'sent' && o.deliver_on === today);
export const toChase = (orders) => (orders || []).filter((o) => o.stage === 'chase');

// "1 keg on order, due Tue 27 Oct".
export function onOrderWords(quantity, unit, due) {
  return `${qty(quantity, unit)} on order${due ? `, due ${deliveryWords(due)}` : ''}`;
}
